var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { history } from 'prosemirror-history';
import { DOMParser } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { BasicCtrlPlugin, BSControlKey } from './basic/basic-ctrl';
import { createCustomCtrlPlugin, CUSTOM_CTRL_ACCEPT } from './basic/custom-ctrl';
import { DecorationPlugin } from './basic/decoration';
import { BASIC_KEYMAP_KEY, createBasicKeymapPlugin, createCustomKeymapPlugins, defaultKeymapPlugin, } from './basic/keymap';
import { createLifeCyclePlugin } from './basic/lifecycle/lifecycle-plugin';
import { ruleBuilder } from './basic/text-shortcut/rule-builder';
import { SHORTCUT_KEY } from './basic/text-shortcut/shortcut-plugin';
import { EventChannel } from './event';
import { handleDOMSpec, removeBrInEnd } from './formatter';
import { parseSylPluginConfig } from './libs/plugin-config-parse';
import { LocaleStore } from './locale';
import { ModuleManager } from './module';
import { BaseCardView, basicSchema, SylPlugin, } from './schema';
import { createSchema, updateSchema } from './schema/normalize';
var EDITOR_CHECK = ['autocomplete', 'autoCorrect', 'autoCapitalize'];
var dispatchTransactionFactory = function (_a) {
    var view = _a.view, emitter = _a.emitter, onError = _a.onError;
    return function (tr) {
        try {
            if (!view || !view.docView || !emitter)
                return;
            var newState = view.state.apply(tr);
            view.updateState(newState);
            emitter.emit(EventChannel.LocalEvent.ON_CHANGE);
        }
        catch (err) {
            onError && onError(err);
        }
    };
};
// Format the property of `$NodeView` in `SylPlugin` as nodeViews` in `prosemirror-plugin-props, and inject `SylApi`
var getNodeViewStringMap = function (sylPlugins, adapter) {
    return sylPlugins.reduce(function (nodeViewMap, sylPlugin) {
        var NodeViewCtor = sylPlugin.$NodeView;
        var $schema = sylPlugin.$schema;
        if (!NodeViewCtor || !$schema)
            return nodeViewMap;
        var name = sylPlugin.name || sylPlugin.$controller.name;
        if (nodeViewMap[name])
            console.warn('multiple register', name);
        nodeViewMap[name] = function (node, editorView, getPos) {
            var nodeView = new NodeViewCtor(adapter, node, editorView, getPos);
            if (nodeView instanceof BaseCardView) {
                nodeView.mount({ ViewMap: $schema.ViewMap, layers: $schema.layers });
                nodeView.afterMount(name);
            }
            return nodeView;
        };
        return nodeViewMap;
    }, {});
};
var setConfiguration = function (baseConfig, configProps, cb) {
    Object.keys(baseConfig).forEach(function (key) {
        if (configProps[key] !== undefined && baseConfig[key] !== configProps[key]) {
            var preValue = baseConfig[key];
            baseConfig[key] = configProps[key];
            cb && cb(key, configProps[key], preValue);
        }
    });
};
var SylConfigurator = /** @class */ (function () {
    function SylConfigurator(mount, sylPluginConfigs, config) {
        var _this = this;
        if (sylPluginConfigs === void 0) { sylPluginConfigs = []; }
        if (config === void 0) { config = {}; }
        // configs of SylPlugin
        this.sylPluginConfigs = [];
        // instances of SylPlugin
        this.sylPluginInstances = [];
        // configuration that pass to BasicCtrlPlugin
        this.basicConfiguration = {
            keepLastLine: true,
            keepMarks: true,
            dropCursor: {},
            placeholder: '',
            keepWhiteSpace: false,
            clickSpacingToInsertLine: true,
        };
        this.extraConfiguration = {
            onError: function (err) {
                throw err;
            },
            autoFocus: false,
            spellCheck: false,
            onBlur: function () { },
            onFocus: function () { },
        };
        this.baseConfiguration = {
            emitter: new EventChannel(),
            locale: {},
            disable: false,
            disableShortcut: false,
        };
        this.customConfiguration = {
            eventHandler: {},
            scrollThreshold: 5,
            scrollMargin: 0,
        };
        this.keymapConfiguration = {};
        // prosemirror-plugin
        this.plugins = [history()];
        // prosemirror-schema
        this.schema = createSchema(basicSchema);
        this.installModule = function (adapter, module) {
            _this.moduleManage = new ModuleManager(adapter, module);
            _this.moduleManage.install();
        };
        this.installSylPlugins = function (adapter) {
            var _a = parseSylPluginConfig(_this.sylPluginConfigs, adapter), sylPlugins = _a.sylPlugins, nativePlugins = _a.nativePlugins;
            _this.initNativePlugin(adapter, sylPlugins, nativePlugins);
            _this.schema = createSchema(updateSchema(_this.schema.spec, sylPlugins.map(function (p) { return p && p.$schemaMeta; }).filter(function (p) { return p; })));
            var newProseState = EditorState.create({
                schema: _this.schema,
                plugins: _this.plugins,
            });
            _this.view.setProps({
                state: newProseState,
                nodeViews: getNodeViewStringMap(sylPlugins, adapter),
                dispatchTransaction: dispatchTransactionFactory({
                    view: _this.view,
                    emitter: _this.baseConfiguration.emitter,
                    onError: _this.extraConfiguration.onError,
                }),
            });
            _this.sylPluginInstances = sylPlugins;
            _this.extraConfiguration.autoFocus && _this.view.focus();
        };
        this.installController = function (adapter, sylControllers) {
            _this.collectCommands(adapter, sylControllers);
            _this.customCtrlPlugin.registerProps(sylControllers);
            _this.customKeyMapPlugin.registerProps(sylControllers.filter(function (c) { return c.keymap; }).map(function (c) { return c.keymap; }));
        };
        this.collectCommands = function (adapter, sylControllers) {
            sylControllers.forEach(function (sylController) {
                (sylController === null || sylController === void 0 ? void 0 : sylController.command) && adapter.addCommand(sylController.name, sylController.command);
            });
        };
        this.registerController = function (name, Controller, controllerProps) {
            var plugin = null;
            _this.sylPluginInstances.some(function (instance) {
                if (instance.name === name) {
                    plugin = instance;
                    plugin.registerController(Controller, controllerProps);
                    return true;
                }
            });
            if (!plugin) {
                plugin = new SylPlugin();
                plugin.name = name;
                plugin.Controller = Controller;
                plugin.init(_this.adapter, { controllerProps: controllerProps });
                _this.sylPluginInstances.push(plugin);
            }
            _this.installController(_this.adapter, [plugin.$controller]);
            _this.emit(EventChannel.LocalEvent.CONFIG_PLUGIN_CHANGE);
        };
        this.unregisterController = function (name) {
            var isChange = _this.sylPluginInstances.some(function (plugin) {
                var _a, _b, _c;
                if (plugin.name === name && plugin.$controller) {
                    (_a = _this.customCtrlPlugin) === null || _a === void 0 ? void 0 : _a.unregisterProps(plugin.$controller);
                    plugin.$controller.keymap && ((_b = _this.customKeyMapPlugin) === null || _b === void 0 ? void 0 : _b.unregisterProps(plugin.$controller.keymap));
                    if (plugin.$controller.command)
                        (_c = _this.adapter) === null || _c === void 0 ? true : delete _c.command[name];
                    plugin.unregisterController();
                    return true;
                }
            });
            isChange && _this.emit(EventChannel.LocalEvent.CONFIG_PLUGIN_CHANGE);
        };
        this.setExtraConfiguration = function (config) {
            return setConfiguration(_this.extraConfiguration, config, function (key, val, oldVal) {
                if (key === 'spellCheck') {
                    EDITOR_CHECK.forEach(function (attr) { return _this.view.dom.setAttribute(attr, val ? 'on' : 'off'); });
                    _this.view.dom.setAttribute('spellcheck', val ? 'true' : 'false');
                }
                else if (key === 'onBlur') {
                    _this.emitter.off(EventChannel.LocalEvent.ON_BLUR, oldVal);
                    _this.emitter.on(EventChannel.LocalEvent.ON_BLUR, val);
                }
                else if (key === 'onFocus') {
                    _this.emitter.off(EventChannel.LocalEvent.ON_FOCUS, oldVal);
                    _this.emitter.on(EventChannel.LocalEvent.ON_FOCUS, val);
                }
            });
        };
        this.setBaseConfiguration = function (config) {
            return setConfiguration(_this.baseConfiguration, config, function (key, val) {
                if (key === 'locale') {
                    _this.localStore = new LocaleStore(val);
                }
                else if (key === 'disable' && _this.view) {
                    _this.setEditable(!val);
                }
            });
        };
        this.setBasicCtrlConfiguration = function (config) {
            return setConfiguration(_this.basicConfiguration, config, function (key, val) {
                if (key === 'placeholder' && _this.view) {
                    _this.view.updateState(_this.view.state);
                }
                else if (key === 'keepMarks' && _this.view) {
                    var _a = _this.view, state = _a.state, dispatch = _a.dispatch;
                    dispatch(state.tr.setMeta(BASIC_KEYMAP_KEY, { keepMarks: val }));
                }
            });
        };
        this.setCustomConfiguration = function (props) {
            var _a;
            Object.keys(props).forEach(function (key) {
                var _a;
                var _b;
                if (CUSTOM_CTRL_ACCEPT[key]) {
                    if (_this.customConfiguration[key]) {
                        (_b = _this.customCtrlPlugin) === null || _b === void 0 ? void 0 : _b.unregisterProps((_a = {}, _a[key] = _this.customConfiguration[key], _a));
                    }
                    // @ts-ignore
                    _this.customConfiguration[key] = props[key];
                }
            });
            (_a = _this.customCtrlPlugin) === null || _a === void 0 ? void 0 : _a.registerProps(_this.customConfiguration, true);
        };
        this.registerEventHandler = function (eventHandler) {
            var _a;
            (_a = _this.customCtrlPlugin) === null || _a === void 0 ? void 0 : _a.registerProps({ eventHandler: eventHandler });
        };
        this.unregisterEventHandler = function (eventHandler) {
            var _a;
            (_a = _this.customCtrlPlugin) === null || _a === void 0 ? void 0 : _a.unregisterProps({ eventHandler: eventHandler });
        };
        this.setKeymapConfiguration = function (keymap) {
            if (_this.keymapConfiguration !== keymap || !keymap)
                _this.unregisterKeymap(_this.keymapConfiguration);
            _this.keymapConfiguration = keymap || {};
            _this.registerKeymap(_this.keymapConfiguration);
        };
        this.registerKeymap = function (keymap) {
            _this.customKeyMapPlugin && _this.customKeyMapPlugin.registerProps(keymap);
        };
        this.unregisterKeymap = function (keymap) {
            _this.customKeyMapPlugin && _this.customKeyMapPlugin.unregisterProps(keymap);
        };
        this.mount = mount;
        this.sylPluginConfigs = sylPluginConfigs;
        this.update(config);
        this.view = new EditorView(this.mount, {
            state: EditorState.create({
                schema: this.schema,
            }),
        });
    }
    Object.defineProperty(SylConfigurator.prototype, "onError", {
        get: function () {
            return this.extraConfiguration.onError;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylConfigurator.prototype, "emitter", {
        get: function () {
            return this.baseConfiguration.emitter;
        },
        enumerable: false,
        configurable: true
    });
    SylConfigurator.prototype.init = function (adapter, module) {
        if (module === void 0) { module = {}; }
        this.adapter = adapter;
        this.customCtrlPlugin = createCustomCtrlPlugin(adapter, [this.customConfiguration]);
        this.customKeyMapPlugin = createCustomKeymapPlugins(adapter, [this.keymapConfiguration]);
        this.installSylPlugins(adapter);
        this.installModule(adapter, module);
        this.constructParser();
    };
    SylConfigurator.prototype.constructParser = function () {
        var _this = this;
        var domParse = DOMParser.fromSchema(this.view.state.schema);
        var originParseSlice = domParse.parseSlice.bind(domParse);
        domParse.parseSlice = function (dom, _option) {
            var keepWhiteSpace = _this.basicConfiguration.keepWhiteSpace;
            var option = _option;
            if (!option)
                option = {};
            if (keepWhiteSpace !== undefined) {
                option.preserveWhitespace = keepWhiteSpace;
            }
            handleDOMSpec(dom);
            var slice = originParseSlice(dom, option);
            removeBrInEnd(slice);
            return slice;
        };
        this.domParser = domParse;
    };
    SylConfigurator.prototype.initNativePlugin = function (adapter, sylPlugins, nativePlugins) {
        var _a;
        var textShortCutPlugin = ruleBuilder(sylPlugins.map(function (p) { return p && p.$schemaMeta; }).filter(function (p) { return p; }), !this.baseConfiguration.disableShortcut);
        this.installController(adapter, sylPlugins.map(function (s) { return s.$controller; }).filter(function (s) { return s; }));
        (_a = this.plugins).push.apply(_a, __spreadArray(__spreadArray(__spreadArray(__spreadArray([], nativePlugins.top, false), [textShortCutPlugin,
            this.customCtrlPlugin,
            // decrease the priority of the `keymap`, because `handleKeyDown` can handle more things
            this.customKeyMapPlugin,
            createBasicKeymapPlugin(this.basicConfiguration),
            defaultKeymapPlugin,
            DecorationPlugin(),
            BasicCtrlPlugin(this.basicConfiguration, !this.baseConfiguration.disable)], false), nativePlugins.bottom, false), [createLifeCyclePlugin(adapter)], false));
    };
    SylConfigurator.prototype.update = function (config) {
        var _a;
        this.setBaseConfiguration(config);
        this.setExtraConfiguration(config);
        this.setBasicCtrlConfiguration(config);
        this.setCustomConfiguration(config);
        this.setKeymapConfiguration(config.keymap);
        config.module && ((_a = this.moduleManage) === null || _a === void 0 ? void 0 : _a.update(config.module));
    };
    SylConfigurator.prototype.on = function (event, handler) {
        return this.baseConfiguration.emitter.on(event, handler);
    };
    SylConfigurator.prototype.off = function (event, handler) {
        return this.baseConfiguration.emitter.off(event, handler);
    };
    SylConfigurator.prototype.emit = function (event) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        return (_a = this.baseConfiguration.emitter).emit.apply(_a, __spreadArray([event], args, false));
    };
    SylConfigurator.prototype.getLocaleValue = function (name) {
        if (!this.localStore)
            return '';
        return this.localStore._get(name);
    };
    SylConfigurator.prototype.setEditable = function (editable) {
        var _a = this.view, state = _a.state, dispatch = _a.dispatch;
        dispatch(state.tr.setMeta(BSControlKey, { editable: editable }));
    };
    SylConfigurator.prototype.setShortcutAble = function (enable) {
        var _a = this.view, state = _a.state, dispatch = _a.dispatch;
        dispatch(state.tr.setMeta(SHORTCUT_KEY, enable));
    };
    SylConfigurator.prototype.setLocale = function (locale) {
        if (!locale)
            return;
        if (!this.localStore)
            this.localStore = new LocaleStore(this.baseConfiguration.locale);
        this.localStore._set(locale);
        return this.emit(EventChannel.LocalEvent.LOCALE_CHANGE);
    };
    SylConfigurator.prototype.getSylPlugins = function () {
        return this.sylPluginInstances;
    };
    SylConfigurator.prototype.uninstall = function () {
        this.emit(EventChannel.LocalEvent.EDITOR_WILL_UNMOUNT);
        this.view.destroy();
        this.moduleManage && this.moduleManage.uninstall();
    };
    return SylConfigurator;
}());
export { SylConfigurator };
//# sourceMappingURL=configurator.js.map